import {DataStatus, Label, isCurrency} from 'platform/components';
import {HStack, Heading, Show, VStack, Text, Icon, ThemeIconKey} from 'platform/foundation';
import {useCurrencySymbolFormatter, useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useShowPaymentPrescriptionQuery} from '@dms/api/finance';
import {Money} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {EMPTY_PLACEHOLDER} from '../../constants/placeholders';

interface PrescriptionIdPriceBoxProps extends TestIdProps {
  prescriptionId: string | Nullish;
  isCorrectiveTaxDocument?: boolean;
}

export function PrescriptionIdPriceBox(props: PrescriptionIdPriceBoxProps) {
  const formatCurrency = useFormatCurrency();
  const formatCurrencySymbol = useCurrencySymbolFormatter();

  const {
    data: prescription,
    isLoading,
    isError,
  } = useShowPaymentPrescriptionQuery(
    {paymentPrescriptionId: props.prescriptionId ?? ''},
    {skip: isNilOrEmpty(props.prescriptionId)}
  );

  const formatSymbol = (val: string | Nullish) =>
    match(isCurrency(val))
      .with(true, always(`(${isCurrency(val) ? formatCurrencySymbol(val) : val})`))
      .otherwise(always(''));

  const formatPrice = (val: Money | Nullish) =>
    isNotNil(val)
      ? (formatCurrency(parseFloat(val.amount), val.currency, 2) ?? EMPTY_PLACEHOLDER)
      : EMPTY_PLACEHOLDER;

  const directionIcon = match<'incoming' | 'outgoing' | undefined, ThemeIconKey | undefined>(
    prescription?.direction
  )
    .with('incoming', always('navigation/arrow_forward'))
    .with('outgoing', always('navigation/arrow_back'))
    .otherwise(always(undefined));

  return (
    <DataStatus isError={isError} isLoading={isLoading} grow={1} spacing={5}>
      <HStack spacing={4}>
        <VStack width={41}>
          <Label>{`${i18n.t('general.labels.amount')} ${formatSymbol(
            prescription?.price?.currency
          )}`}</Label>
          <Heading size={4} data-testid={suffixTestId('amountToPay', props)}>
            {formatPrice(prescription?.price)}
          </Heading>
        </VStack>

        <Show when={prescription?.state !== 'completed'}>
          <VStack width={41}>
            <Label>{`${i18n.t('entity.invoice.labels.remaining')} ${formatSymbol(
              prescription?.remainsToPay?.currency
            )}`}</Label>
            <Heading size={4} color="danger" data-testid={suffixTestId('remainsToPay', props)}>
              {formatPrice(prescription?.remainsToPay)}
            </Heading>
          </VStack>
        </Show>
        <Show when={prescription?.direction}>
          <VStack width={41}>
            <Label>{i18n.t('general.labels.direction')}</Label>
            <HStack align="center" spacing={1}>
              <Icon value={directionIcon} size={3} />
              <Text data-testid={suffixTestId('paymentDirection', props)}>
                {i18n.t(`entity.prescription.labels.${prescription?.direction}`)}
              </Text>
            </HStack>
          </VStack>
        </Show>
      </HStack>
    </DataStatus>
  );
}
